import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';

export interface MentionItem {
  id: string;
  label: string;
}

export interface MentionSuggestionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

interface MentionSuggestionListProps {
  items: MentionItem[];
  command: (item: MentionItem) => void;
}

export const MentionSuggestionList = forwardRef<
  MentionSuggestionListRef,
  MentionSuggestionListProps
>(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((i) => (i + items.length - 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((i) => (i + 1) % items.length);
        return true;
      }
      if (event.key === 'Enter') {
        if (items[selectedIndex]) {
          command(items[selectedIndex]);
        }
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) {
    return (
      <Box
        sx={{
          p: 1.5,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 3,
          minWidth: 200,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          No entities found
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: 3,
        minWidth: 200,
        maxHeight: 240,
        overflowY: 'auto',
      }}
    >
      <List dense disablePadding>
        {items.map((item, index) => (
          <ListItemButton
            key={item.id}
            selected={index === selectedIndex}
            onClick={() => command(item)}
            sx={{ py: 0.5 }}
          >
            <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
});

MentionSuggestionList.displayName = 'MentionSuggestionList';
